import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthHeader from "../components/AuthHeader";
import "../css/Signup.css";
import {
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaPhoneAlt,
  FaUser
} from "react-icons/fa";

function Signup() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });

  const [message, setMessage] = useState("");
  const [memberId, setMemberId] = useState("");
  const [loading, setLoading] = useState(false);

  const getPasswordStrength = (password) => {
    let score = 0;

    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (password.length === 0) {
      return { label: "", className: "" };
    } else if (score <= 2) {
      return { label: "Weak password", className: "weak" };
    } else if (score === 3 || score === 4) {
      return { label: "Medium password", className: "medium" };
    } else {
      return { label: "Strong password", className: "strong" };
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const showTopMessage = (text, isSuccess = false) => {
    setMessage(text);

    if (!isSuccess) {
      setMemberId("");
    }

    setTimeout(() => {
      setMessage("");
    }, 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setMemberId("");

    if (formData.password !== formData.confirmPassword) {
      showTopMessage("Password and confirm password do not match");
      return;
    }

    if (formData.password.length < 8) {
      showTopMessage("Password must be at least 8 characters");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/backend/signup.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const rawText = await response.text();
      console.log("RAW RESPONSE:", rawText);

      let data;

      try {
        data = JSON.parse(rawText);
      } catch (jsonError) {
        const jsonStart = rawText.indexOf("{");
        const jsonEnd = rawText.lastIndexOf("}");

        if (jsonStart !== -1 && jsonEnd !== -1) {
          try {
            const cleanJson = rawText.slice(jsonStart, jsonEnd + 1);
            data = JSON.parse(cleanJson);
          } catch (innerError) {
            if (rawText.includes("Email already registered")) {
              showTopMessage("Email already registered");
            } else if (rawText.includes("Phone number already registered")) {
              showTopMessage("Phone number already registered");
            } else if (rawText.includes("Account created successfully")) {
              showTopMessage("Account created successfully", true);
            } else {
              showTopMessage("Something went wrong");
            }
            return;
          }
        } else {
          if (rawText.includes("Email already registered")) {
            showTopMessage("Email already registered");
          } else if (rawText.includes("Phone number already registered")) {
            showTopMessage("Phone number already registered");
          } else if (rawText.includes("Account created successfully")) {
            showTopMessage("Account created successfully", true);
          } else {
            showTopMessage("Something went wrong");
          }
          return;
        }
      }

      if (data.success) {
        showTopMessage(data.message || "Account created successfully", true);
        setMemberId(data.member_id || "");
        setFormData({
          name: "",
          email: "",
          phone: "",
          password: "",
          confirmPassword: ""
        });

        setTimeout(() => {
          navigate("/login");
        }, 2500);
      } else {
        showTopMessage(data.message || "Signup failed");
      }
    } catch (error) {
      showTopMessage("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {message && (
        <div className={`top-alert ${memberId ? "success" : "error"}`}>
          {message}
        </div>
      )}

      <div className="signup-page">
        <div className="signup-layout">
          <div className="signup-left">
            <div className="brand-badge">Mr.Fillo</div>
            <h1>Join Mr.Fillo today</h1>
            <p>
              Create your account to access a smarter, faster, and more secure
              experience built for modern users.
            </p>

            <div className="left-feature-list">
              <div className="left-feature-card">
                <h3>Fast signup</h3>
                <p>Create your account in a few simple steps.</p>
              </div>

              <div className="left-feature-card">
                <h3>Unique Member ID</h3>
                <p>Get your own secure Member ID for login access.</p>
              </div>

              <div className="left-feature-card">
                <h3>Secure authentication</h3>
                <p>
                  Protected login with password validation and uniqueness checks.
                </p>
              </div>
            </div>
          </div>

          <div className="signup-right">
            <div className="signup-card">
              <AuthHeader
                title="Create Account"
                subtitle="Create your Mr.Fillo account"
              />

              <form onSubmit={handleSubmit}>
                <div className="input-group">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <FaPhoneAlt className="input-icon" />
                  <input
                    type="text"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                {formData.password && (
                  <div className={`password-strength ${passwordStrength.className}`}>
                    {passwordStrength.label}
                  </div>
                )}

                <div className="password-field">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <button type="submit" disabled={loading}>
                  {loading ? "Creating..." : "Create Account"}
                </button>
              </form>

              {memberId && (
                <div className="member-id-card">
                  <div className="member-success-title">
                    🎉 Account Created Successfully
                  </div>
                  <div className="member-id-text">Your Member ID</div>
                  <div className="member-id-number">{memberId}</div>
                  <div className="member-note">
                    Use this Member ID to login to Mr.Fillo
                  </div>
                </div>
              )}

              <div className="signup-footer">
                Already have an account?{" "}
                <span onClick={() => navigate("/login")}>Login</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Signup;